import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { CloseOutlined, RightOutlined } from "@ant-design/icons";
import { useSelector } from "react-redux";
import "./ModalBreakingSound.scss";
import ModalImage from "shared/assets/images/modal-breaking-sound-home.png";
import iconPlaneRainbow from "shared/assets/images/icon-plane-rainbow.svg";
import {
  dataLayerClickedEvent,
  dataLayerDialogViewedEvent,
  tagManagerEvent,
} from "event-tracker/gtm-events";
import { HomeButtonClickEventName } from "event-tracker/button-click-event-name";
import { ArtistState } from "explorer/reducers/artistas";
import { UserState } from "explorer/reducers/users";
import { allowedTouringArtist } from "shared/helpers/touring-permissions";
import { getRefPageLocalStorageArray } from "shared/helpers/ref-page";
import { HomeDialogEventName } from "event-tracker/dialog-event-name";
import { useHistory } from "react-router-dom";

const eventConfig = (isElegible: boolean) => {
  return {
    ...tagManagerEvent.Home.S4A_HOME_DIAGNOSED,
    businessContext: "HOME_V2",
    dialogName: isElegible
      ? "BREAKING_SOUND_ELEGIBLE"
      : "BREAKING_SOUND_INELEGIBLE",
  };
};

const ModalBreakingSound = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const [isModalBreakingSoundOpen, setIsModalBreakingSoundOpen] =
    useState<boolean>(false);

  const { loading } = useSelector((state: UserState) => state.users);
  const { userArtist, loading: loadingArtist } = useSelector(
    (state: ArtistState) => state.artists
  );

  const level = userArtist.stage || 0;

  const isElegible = allowedTouringArtist(userArtist?.country || "");

  useEffect(() => {
    const hasSeenBreakingSoundModal = localStorage.getItem(
      "hasSeenBreakingSoundModal"
    );

    if (hasSeenBreakingSoundModal === "true") return;

    const refPages = getRefPageLocalStorageArray();
    const isFromBreakingSound = refPages.some((ref) =>
      ref?.toLowerCase().includes("breakingsound")
    );

    if (!loading && !loadingArtist && level > 0 && isFromBreakingSound) {
      setIsModalBreakingSoundOpen(true);
    }
  }, [loading, loadingArtist, level]);

  useEffect(() => {
    if (isModalBreakingSoundOpen) {
      const event = eventConfig(isElegible);
      if (isElegible) {
        dataLayerDialogViewedEvent(
          HomeDialogEventName.BREAKING_SOUND_ELEGIBLE,
          event
        );
      } else {
        dataLayerDialogViewedEvent(
          HomeDialogEventName.BREAKING_SOUND_INELEGIBLE,
          event
        );
      }
    }
  }, [isModalBreakingSoundOpen, isElegible]);

  const handleClose = () => {
    setIsModalBreakingSoundOpen(false);
    localStorage.setItem("hasSeenBreakingSoundModal", "true");
  };

  const handleCTA = () => {
    const event = eventConfig(isElegible);

    dataLayerClickedEvent(
      HomeButtonClickEventName.BREAKING_SOUND_SUBMIT,
      event
    );

    handleClose();
    if (isElegible) {
      history.push("/touring");
    }
  };

  return (
    <AnimatePresence>
      {isModalBreakingSoundOpen && (
        <>
          <motion.div
            className="modal-breaking-sound-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleClose}
          />
          <motion.div
            id="modal-breaking-sound"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            transition={{ duration: 0.3 }}
          >
            <button className="close-button" onClick={handleClose}>
              <CloseOutlined />
            </button>

            <img
              src={ModalImage}
              alt="Breaking Sound"
              className="modal-header-image"
            />

            <div className="modal-content">
              <div className="icon-container">
                <img src={iconPlaneRainbow} alt="Icon" />
              </div>

              <h2 className="modal-title">
                <Trans
                  i18nKey="pages.Home.ModalBreakingSound.title"
                  components={{
                    breaking: <span className="breaking-text" />,
                  }}
                />
              </h2>

              {isElegible ? (
                <>
                  <div className="modal-benefits">
                    <p className="modal-description">
                      {t("pages.Home.ModalBreakingSound.description")}
                    </p>
                    <div className="benefit-item">
                      <span className="check-circle" />
                      <span>{t("pages.Home.ModalBreakingSound.benefit1")}</span>
                    </div>
                    <div className="benefit-item">
                      <span className="check-circle" />
                      <span>{t("pages.Home.ModalBreakingSound.benefit2")}</span>
                    </div>
                    <div className="benefit-item">
                      <span className="check-circle" />
                      <span>{t("pages.Home.ModalBreakingSound.benefit3")}</span>
                    </div>
                  </div>

                  <button className="modal-cta-button" onClick={handleCTA}>
                    {t("pages.Home.ModalBreakingSound.ctaElegible")}
                    <RightOutlined style={{ width: "12px", height: "12px" }} />
                  </button>
                </>
              ) : (
                <>
                  <div className="not-elegible-message">
                    <p>
                      {t("pages.Home.ModalBreakingSound.notElegibleMessage")}
                    </p>
                  </div>

                  <button className="modal-cta-button" onClick={handleCTA}>
                    {t("pages.Home.ModalBreakingSound.ctaNotElegible")}
                    <RightOutlined style={{ width: "12px", height: "12px" }} />
                  </button>
                </>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default ModalBreakingSound;
